import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Shield, Cookie, Settings } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface CookieSettings {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface ConsentSettings {
  banner_title: string;
  banner_description: string;
  necessary_title: string;
  necessary_description: string;
  analytics_title: string;
  analytics_description: string;
  marketing_title: string;
  marketing_description: string;
  accept_all_text: string;
  accept_selected_text: string;
  reject_all_text: string;
  settings_text: string;
}

const defaultSettings: Record<string, ConsentSettings> = {
  nl: {
    banner_title: "Cookie voorkeuren",
    banner_description: "Wij gebruiken cookies om uw ervaring op onze website te verbeteren.",
    necessary_title: "Noodzakelijke cookies",
    necessary_description: "Deze cookies zijn essentieel voor het functioneren van de website.",
    analytics_title: "Analytische cookies",
    analytics_description: "Deze cookies helpen ons te begrijpen hoe bezoekers de website gebruiken.",
    marketing_title: "Marketing cookies",
    marketing_description: "Deze cookies worden gebruikt voor gepersonaliseerde advertenties.",
    accept_all_text: "Alles accepteren",
    accept_selected_text: "Selectie accepteren",
    reject_all_text: "Alleen noodzakelijk",
    settings_text: "Cookie instellingen",
  },
  en: {
    banner_title: "Cookie preferences",
    banner_description: "We use cookies to enhance your experience on our website.",
    necessary_title: "Necessary cookies",
    necessary_description: "These cookies are essential for the website to function.",
    analytics_title: "Analytics cookies",
    analytics_description: "These cookies help us understand how visitors use our website.",
    marketing_title: "Marketing cookies",
    marketing_description: "These cookies are used to make advertisements more relevant.",
    accept_all_text: "Accept all",
    accept_selected_text: "Accept selected",
    reject_all_text: "Necessary only",
    settings_text: "Cookie settings",
  },
  fr: {
    banner_title: "Préférences de cookies",
    banner_description: "Nous utilisons des cookies pour améliorer votre expérience.",
    necessary_title: "Cookies nécessaires",
    necessary_description: "Ces cookies sont essentiels au fonctionnement du site.",
    analytics_title: "Cookies analytiques",
    analytics_description: "Ces cookies nous aident à comprendre comment les visiteurs utilisent notre site.",
    marketing_title: "Cookies marketing",
    marketing_description: "Ces cookies sont utilisés pour rendre les publicités plus pertinentes.",
    accept_all_text: "Tout accepter",
    accept_selected_text: "Accepter la sélection",
    reject_all_text: "Nécessaires uniquement",
    settings_text: "Paramètres des cookies",
  },
};

const COOKIE_CONSENT_KEY = "cookie_consent";
const CONSENT_ID_KEY = "consent_id";

const generateConsentId = () => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
};

// Get language from localStorage or browser
const getLanguage = (): string => {
  const storedLang = localStorage.getItem("language");
  if (storedLang && ["nl", "en", "fr"].includes(storedLang)) {
    return storedLang;
  }
  const browserLang = navigator.language.split("-")[0];
  if (["nl", "en", "fr"].includes(browserLang)) {
    return browserLang;
  }
  return "nl";
};

const CookieConsent = () => {
  const [currentLanguage, setCurrentLanguage] = useState(getLanguage());
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState<CookieSettings>({
    necessary: true,
    analytics: false,
    marketing: false,
  });
  const [consentSettings, setConsentSettings] = useState<ConsentSettings>(
    defaultSettings[currentLanguage] || defaultSettings.nl
  );

  // Listen for language changes
  useEffect(() => {
    const handleStorageChange = () => {
      setCurrentLanguage(getLanguage());
    };
    
    window.addEventListener("storage", handleStorageChange);
    
    // Also check periodically for language changes within the same tab
    const interval = setInterval(() => {
      const newLang = getLanguage();
      if (newLang !== currentLanguage) {
        setCurrentLanguage(newLang);
      }
    }, 1000);
    
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, [currentLanguage]);

  useEffect(() => {
    // Check if consent already given
    const storedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!storedConsent) {
      setShowBanner(true);
    } else {
      try {
        const parsed = JSON.parse(storedConsent);
        setSettings(parsed);
      } catch {
        setShowBanner(true);
      }
    }

    // Fetch consent settings from database
    const fetchSettings = async () => {
      const { data } = await supabase
        .from("cookie_consent_settings")
        .select("*")
        .eq("language_code", currentLanguage)
        .single();

      if (data) {
        setConsentSettings(data as ConsentSettings);
      } else {
        setConsentSettings(defaultSettings[currentLanguage] || defaultSettings.nl);
      }
    };

    fetchSettings();
  }, [currentLanguage]);

  const saveConsent = async (consentSettings: CookieSettings) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentSettings));
    
    // Get or create consent ID
    let consentId = localStorage.getItem(CONSENT_ID_KEY);
    if (!consentId) {
      consentId = generateConsentId();
      localStorage.setItem(CONSENT_ID_KEY, consentId);
    }

    // Save to database
    try {
      await supabase.from("cookie_consents").upsert({
        consent_id: consentId,
        necessary: consentSettings.necessary,
        analytics: consentSettings.analytics,
        marketing: consentSettings.marketing,
      }, { onConflict: "consent_id" });
    } catch (error) {
      console.error("Error saving consent:", error);
    }

    setSettings(consentSettings);
    setShowBanner(false);
    setShowSettings(false);

    // Dispatch event for analytics tracking
    window.dispatchEvent(new CustomEvent("cookieConsentUpdated", { 
      detail: consentSettings 
    }));
  };

  const handleAcceptAll = () => {
    saveConsent({ necessary: true, analytics: true, marketing: true });
  };

  const handleAcceptSelected = () => {
    saveConsent(settings);
  };

  const handleRejectAll = () => {
    saveConsent({ necessary: true, analytics: false, marketing: false });
  };

  const openSettings = () => {
    setShowSettings(true);
  };

  // Also provide a way to open settings from footer
  useEffect(() => {
    const handleOpenSettings = () => {
      setShowBanner(true);
      setShowSettings(true);
    };

    window.addEventListener("openCookieSettings", handleOpenSettings);
    return () => window.removeEventListener("openCookieSettings", handleOpenSettings);
  }, []);

  if (!showBanner) return null;

  // SEO-friendly banner: positioned at bottom, doesn't block content
  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 p-4 pointer-events-none"
      role="dialog"
      aria-label={consentSettings.banner_title}
      aria-modal="false"
    >
      <Card className="max-w-2xl mx-auto p-4 sm:p-6 shadow-lg border pointer-events-auto">
        <div className="flex items-start gap-3 mb-3">
          <div className="p-2 rounded-full bg-primary/10 shrink-0">
            <Cookie className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold">{consentSettings.banner_title}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {consentSettings.banner_description}
            </p>
          </div>
        </div>

        {showSettings && (
          <div className="space-y-3 mb-4">
            {/* Necessary Cookies */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-primary shrink-0" />
                  <Label className="font-medium text-sm">{consentSettings.necessary_title}</Label>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {consentSettings.necessary_description}
                </p>
              </div>
              <Switch checked={true} disabled className="ml-3 shrink-0" />
            </div>

            {/* Analytics Cookies */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Settings className="h-4 w-4 text-primary shrink-0" />
                  <Label className="font-medium text-sm">{consentSettings.analytics_title}</Label>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {consentSettings.analytics_description}
                </p>
              </div>
              <Switch
                checked={settings.analytics}
                onCheckedChange={(checked) => setSettings({ ...settings, analytics: checked })}
                className="ml-3 shrink-0"
              />
            </div>

            {/* Marketing Cookies */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Cookie className="h-4 w-4 text-primary shrink-0" />
                  <Label className="font-medium text-sm">{consentSettings.marketing_title}</Label>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {consentSettings.marketing_description}
                </p>
              </div>
              <Switch
                checked={settings.marketing}
                onCheckedChange={(checked) => setSettings({ ...settings, marketing: checked })}
                className="ml-3 shrink-0"
              />
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {showSettings ? (
            <>
              <Button variant="outline" onClick={handleRejectAll} size="sm" className="flex-1 min-w-[120px]">
                {consentSettings.reject_all_text}
              </Button>
              <Button variant="outline" onClick={handleAcceptSelected} size="sm" className="flex-1 min-w-[120px]">
                {consentSettings.accept_selected_text}
              </Button>
              <Button onClick={handleAcceptAll} size="sm" className="flex-1 min-w-[120px]">
                {consentSettings.accept_all_text}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleRejectAll} size="sm" className="flex-1 min-w-[100px]">
                {consentSettings.reject_all_text}
              </Button>
              <Button variant="outline" onClick={openSettings} size="sm" className="flex-1 min-w-[100px]">
                <Settings className="h-4 w-4 mr-1" />
                {consentSettings.settings_text}
              </Button>
              <Button onClick={handleAcceptAll} size="sm" className="flex-1 min-w-[100px]">
                {consentSettings.accept_all_text}
              </Button>
            </>
          )}
        </div>
      </Card>
    </div>
  );
};

export default CookieConsent;
